/**
 * Modal Component
 *
 * Reusable modal dialog for confirmations and messages.
 * Supports theme colors and click-outside-to-close.
 */

import React, { useEffect } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  variant?: 'info' | 'warning' | 'danger';
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Cancel',
  onConfirm,
  variant = 'info',
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const backdropStyles: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalStyles: React.CSSProperties = {
    backgroundColor: 'var(--bg-card, #ffffff)',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid var(--border-color, #dadce0)',
  };

  const titleStyles: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '12px',
    color: 'var(--text-primary, #202124)',
  };

  const messageStyles: React.CSSProperties = {
    fontSize: '14px',
    lineHeight: '1.5',
    marginBottom: '24px',
    color: 'var(--text-secondary, #5f6368)',
    whiteSpace: 'pre-wrap', // Preserve line breaks
  };

  const buttonGroupStyles: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  };

  const buttonBaseStyles: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 500,
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const confirmButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    backgroundColor:
      variant === 'danger'
        ? '#ea4335'
        : variant === 'warning'
        ? '#fbbc05'
        : '#4285f4',
    color: '#fff',
  };

  const cancelButtonStyles: React.CSSProperties = {
    ...buttonBaseStyles,
    backgroundColor: 'var(--bg-secondary, #f1f3f4)',
    color: 'var(--text-primary, #202124)',
    border: '1px solid var(--border-color, #dadce0)',
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={backdropStyles} onClick={handleBackdropClick}>
      <div style={modalStyles} onClick={(e) => e.stopPropagation()}>
        <h2 style={titleStyles}>{title}</h2>
        <p style={messageStyles}>{message}</p>
        <div style={buttonGroupStyles}>
          {onConfirm && (
            <button style={cancelButtonStyles} onClick={onClose}>
              {cancelText}
            </button>
          )}
          <button
            style={confirmButtonStyles}
            onClick={handleConfirm}
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
